import { create } from 'zustand';
import { useQuery } from '@tanstack/react-query';
import { getTenantConfig } from './api';

/**
 * Member portal light/dark mode.
 *
 * Two things move together on <html>: the `dark` class (which switches every
 * Tailwind `dark:` variant and the `.dark` token block in `@mlm/ui`) and the
 * tenant's inline theme vars, which `useTenantTheme` re-applies from the theme's
 * dark set when this store flips (inline styles outrank `.dark`, so the class
 * alone would leave a themed tenant's surfaces pinned light).
 *
 * The preference is per-device, so it lives in localStorage rather than the
 * profile — same shape as `member.sidebarCollapsed` in `components/layout.tsx`.
 * `index.html` carries a matching pre-paint guard (baked by `vite.config.ts`
 * for tenants that ship a dark set) so a returning dark user never flashes light.
 */
const STORAGE_KEY = 'member.themeMode';

type ThemeMode = 'light' | 'dark';

function readStored(): ThemeMode {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

function applyMode(mode: ThemeMode) {
  document.documentElement.classList.toggle('dark', mode === 'dark');
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* private mode / storage disabled — the class still applies for this session */
  }
}

interface ThemeModeState {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggle: () => void;
}

export const useThemeMode = create<ThemeModeState>((set, get) => ({
  mode: readStored(),
  setMode: (mode) => {
    applyMode(mode);
    set({ mode });
  },
  toggle: () => get().setMode(get().mode === 'dark' ? 'light' : 'dark'),
}));

/**
 * Whether this tenant may offer the toggle at all: only a theme that ships a
 * dark token set can go dark honestly (see `ThemeTokens.dark`). Reads the shared
 * `['tenant-config']` query, so it costs no extra request.
 */
export function useDarkAvailable(): boolean {
  const { data } = useQuery({ queryKey: ['tenant-config'], queryFn: getTenantConfig, retry: 0 });
  return !!data?.theme?.dark;
}
